import moment from 'moment';
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Button } from 'react-bootstrap/lib';
import { isEmpty } from 'lodash';
import { List } from 'immutable';
import { routerShape } from 'react-router/lib/PropTypes';

import BaseModal from '../general/BaseModal';
import useStoreState from '../../stores/useStoreState';
import Checkbox from '../form/Checkbox';
import PassengerAssistStore from '../../stores/PassengerAssistStore';
import trainTime from '../../lib/TrainTime';
import urls from '../../urls';
import { AccountPages } from '../../constants/AppConstants';
import { AccessibilityCircleIcon } from '../icons/AccessibilityCircleIcon';
import AssistedTravel from '../../records/passengerAssist/AssistedTravel';
import PassengerAssistActions from '../../actions/PassengerAssistActions';
import { getPAModalJourneyData, normalizeJourney } from './passengerAssist/utils';
import { translate } from '../../lib/I18n';
import { usePassengerAssistBookingModal } from '../../hooks/passengerAssist/usePassengerAssistBookingModal';
import { AlertIcon } from '../icons/AlertIcon';
import Journey from '../../records/Journey';
import Trip from '../../records/Trip';
import Radio from '../form/Radio';
import Loading from '../general/Loading';


function PassengerAssistBookingModal(props) {
    const { onClose, onContinue, router, currentJourneyData } = props;

    const { journeys, pendingTravelAssistRequests } = usePassengerAssistBookingModal({ currentJourneyData });
    const passengerAssist = useStoreState(PassengerAssistStore);

    const [selectedTrips, setSelectedTrips] = useState([]);
    const [warning, setShowWarning] = useState('');

    const [isLoading, setIsLoading] = useState(false);

    const {
        widgetToggled,
        accessibilityProfileForm: {
            participateImprovementSurvey,
        }
    } = passengerAssist;

    useEffect(() => {
        if (!widgetToggled) {
            onContinue();
        }
    }, [widgetToggled, onContinue]);

    // Set Passenger Assist trips from selected journeys
    const setPATrips = () => {
        if (selectedTrips.length > 0) {
            const combinedSelectedTrips = selectedTrips
            .map(t => {
                const { direction, tripId } = t;

                const matchingTrip = selectedTrips.find(tr => tr.tripId === tripId && tr.direction !== direction);

                if (!matchingTrip) {
                    return t;
                }

                const combined = {
                    ...t,
                    returnJourney: matchingTrip.returnJourney,
                };

                return combined;
            }).filter(t => {
                const { direction, tripId } = t;

                const hasMatchingTrip = !!(selectedTrips.find(tr => tr.tripId === tripId && tr.direction !== direction));

                if (t.direction === 'Return' && hasMatchingTrip) {
                    return false;
                } else {
                    return true;
                }
            });

            const tripsForPassengerAssist = combinedSelectedTrips.map(i => {
                const {
                    outwardJourney,
                    returnJourney,
                    trip,
                } = i;

                const outwardEnabled = !isEmpty(outwardJourney);
                const returnEnabled = !isEmpty(returnJourney);

                const departureTime = outwardEnabled ? outwardJourney.getDepartureDateTime() : returnJourney.getDepartureDateTime();

                let normalisedOutwardJourney = {};
                let normalisedReturnJourney = {};

                const isSingle = trip.isSingle();
                const isOpenReturn = trip.isOpenReturn();

                normalisedOutwardJourney = normalizeJourney(outwardEnabled ? outwardJourney : trip.outwardJourney.journey);

                if (!isSingle && !isOpenReturn) {
                    normalisedReturnJourney = normalizeJourney(returnEnabled ? returnJourney : trip.returnJourney.journey);
                }

                return new AssistedTravel({
                    departureDate: departureTime,
                    outwardJourney: normalisedOutwardJourney,
                    returnJourney: normalisedReturnJourney,
                    outwardEnabled,
                    returnEnabled,
                });
            });

            PassengerAssistActions.setPendingTravelAssistRequests(new List(tripsForPassengerAssist));
       }
    };

    const renderJourney = ({
        trip,
        journey,
        direction = 'Outward'
    }) => {
        if (!journey) {
            return null;
        }

        const { origin, destination, departureTime, arrivalTime } = getPAModalJourneyData(journey);

        const legs = journey.legs || journey.journey.legs;

        const isTripSelected = !!selectedTrips.find(t => t.journey.id === journey.id);

        const updateSelection = () => {
            const journeyRecord = journey.journey || journey;

            if (isTripSelected) {
                setSelectedTrips(selectedTrips.filter(t => t.journey.id !== journey.id));
            } else {
                setShowWarning('');
                setSelectedTrips([...selectedTrips, {
                    trip,
                    journey,
                    journeyId: journey.id,
                    outwardJourney: direction === 'Outward' ? journeyRecord : {},
                    returnJourney: direction === 'Return' ? journeyRecord : {},
                    direction
                }]);
            }
        };

        const onChange = () => {
            updateSelection();
        };

        return (<li>
            <div className="passenger-assist-trip-summary">
                <div className="passenger-assist-trip-summary__times">
                    <div className="passenger-assist-trip-summary__first-row">
                        <Checkbox
                            className="trip-pa-select-checkbox"
                            label=""
                            aria-label=""
                            onChange={onChange}
                            checked={isTripSelected}
                        />
                        <span className="passenger-assist-trip-summary__direction">
                            <strong>{direction === 'Outward' ? 'Outbound' : direction}</strong>:
                        </span>
                        <span className="passenger-assist-trip-summary__date">
                            {departureTime && trainTime(departureTime).format('ddd D MMM YYYY')
                        }</span>
                    </div>
                </div>
                <Row>
                    <Col xs={8}>
                        <div className="passenger-assist-trip-summary__stations">
                            <div className="passenger-assist-trip-summary__origin-info">
                                <div className="passenger-assist-trip-summary__origin-info__time">
                                    <strong>{departureTime && departureTime.format('HH:mm')}</strong>
                                </div>
                                <div className="passenger-assist-trip-summary__origin-info__station">{origin.name}</div>
                            </div>
                            <img className="arrow-icon" src="/images/arrow-icon.svg" alt="Arrow icon" />
                            <div className="passenger-assist-trip-summary__destination-info">
                                <div className="passenger-assist-trip-summary__destination-info__time">
                                    <strong>{arrivalTime && arrivalTime.format('HH:mm')}</strong>
                                </div>
                                <div className="passenger-assist-trip-summary__destination-info__station">{destination.name}</div>
                            </div>
                        </div>
                    </Col>
                    <Col xs={4}>
                        <div className="passenger-assist-trip-summary__leg-info">
                            {legs && legs.size > 1 ?
                                <span className="passenger-assist-trip-summary__badge passenger-assist-trip-summary__badge--multileg">{translate('account.passenger-assist.book-passenger-assist.multi-leg')}</span> :
                                <span className="passenger-assist-trip-summary__badge passenger-assist-trip-summary__badge--direct">{translate('account.passenger-assist.book-passenger-assist.direct')}</span>
                            }
                        </div>
                    </Col>
                </Row>
            </div>
        </li>);
    };

    const renderPassengerAssistRequest = (req) => {
        const {
            outwardJourney,
            returnJourney,
        } = req;

        const data = [
            {journey: outwardJourney, direction: 'Outward'},
            {journey: returnJourney, direction: 'Return'}
        ];

        return data.map(j => {
            const { direction, journey } = j;

            if (isEmpty(journey)) {
                return null;
            }

            const {
                arrivalHour,
                arrivalMinutes
            } = journey;

            const onChange = () => {
                PassengerAssistActions.setPendingTravelAssistRequestEnabled({
                    orderTripId: req.orderTripId,
                    enabled: direction === 'Outward' ? !req.outwardEnabled : !req.returnEnabled,
                    direction,
                });
            };

            const {
                outwardEnabled,
                returnEnabled
            } = req;

            const legs = direction === 'Outward' ? req.outwardJourney.legs : req.returnJourney.legs;

            const checked = direction === 'Outward' ? outwardEnabled : returnEnabled;

            return (<li>
                <div className="passenger-assist-trip-summary">
                    <div className="passenger-assist-trip-summary__times">
                        <div className="passenger-assist-trip-summary__first-row">
                        <Checkbox
                                className="trip-pa-select-checkbox"
                                label=""
                                aria-label=""
                                onChange={onChange}
                                checked={checked}
                            />
                            <span className="passenger-assist-trip-summary__direction">
                                <strong>{direction === 'Outward' ? 'Outbound' : direction}</strong>:
                            </span>
                            <span className="passenger-assist-trip-summary__date">
                                {moment(direction === 'Outward' ? req.outwardJourney.departureDate : req.returnJourney.departureDate, 'DD/MM/YYYY').format('ddd D MMM YYYY')}
                            </span>
                        </div>
                    </div>
                    <Row>
                        <Col xs={8}>
                            <div className="passenger-assist-trip-summary__stations">
                                <div className="passenger-assist-trip-summary__origin-info">
                                    <div className="passenger-assist-trip-summary__origin-info__time">
                                        <strong>{direction === 'Outward' ?
                                            req.outwardJourney.departureHour + ":" + req.outwardJourney.departureMinutes :
                                            req.returnJourney.departureHour + ":" + req.returnJourney.departureMinutes }
                                        </strong>
                                    </div>
                                   <div className="passenger-assist-trip-summary__origin-info__station">{direction === 'Outward' ? req.outwardJourney.origin.name : req.returnJourney.origin.name}</div>
                                </div>
                                <img className="arrow-icon" src="/images/arrow-icon.svg" alt="Arrow icon" />
                                <div className="passenger-assist-trip-summary__destination-info">
                                    <div className="passenger-assist-trip-summary__destination-info__time">
                                        <strong>{arrivalHour + ':' + arrivalMinutes}</strong>
                                    </div>
                                    <div className="passenger-assist-trip-summary__destination-info__station">{direction === 'Outward' ? req.outwardJourney.destination.name : req.returnJourney.destination.name}</div>
                                </div>
                            </div>
                        </Col>
                        <Col xs={4}>
                            <div className="passenger-assist-trip-summary__leg-info">
                                {legs && legs.length > 1 ?
                                    <span className="passenger-assist-trip-summary__badge passenger-assist-trip-summary__badge--multileg">{translate('account.passenger-assist.book-passenger-assist.multi-leg')}</span> :
                                    <span className="passenger-assist-trip-summary__badge passenger-assist-trip-summary__badge--direct">{translate('account.passenger-assist.book-passenger-assist.direct')}</span>
                                }
                            </div>
                        </Col>
                    </Row>
                </div>
            </li>);
        });
    };

    const viewAccessibilityProfile = () => {
        onClose();
        PassengerAssistActions.setShowBackToSearchResultsButton(true);
        router.push(urls.location(AccountPages.ACCESSIBILITY_PROFILE));
    };

    if (!widgetToggled) {
        return null;
    }

    const onSubmit = () => {
        if (isLoading) {
            return false;
        }

        const enabledReqs = pendingTravelAssistRequests.filter(r => r.outwardEnabled || r.returnEnabled);

        if (!selectedTrips.length && enabledReqs.size < 1) {
            setShowWarning(translate('passenger-assist.modal.no-trips.warning'));
            return false;
        }

        if (typeof participateImprovementSurvey !== 'boolean') {
            setShowWarning(translate('passenger-assist.modal.no-contact-answer.warning'));
            return false;
        }

        PassengerAssistActions.setAccessibilityProfileForm({ participateImprovementSurvey });

        setIsLoading(true);

        setPATrips(selectedTrips);

        onContinue();

        return true;
    };

    const onChangeParticipateImprovementSurvey = (value) => () => {
        PassengerAssistActions.setAccessibilityProfileForm({
            participateImprovementSurvey: value
        });
        setShowWarning('');
    };

    const modalProps = {
        ...props,
        onContinue: onSubmit,
    };

    return (
        <BaseModal className="modal--xs passenger-assist-booking-modal" { ...modalProps }>
            {isLoading ? <Loading className="margin-top" unwrapped standalone /> : (<form>
                <div>
                    <section>
                        <Row>
                            <Col xs={ 12 }>
                                <Row>
                                    <Col xs={1}>
                                        <AccessibilityCircleIcon />
                                    </Col>
                                    <Col xs={11}>
                                        <div>
                                            <Button
                                                bsStyle="link"
                                                className="view-accessibility-profile"
                                                onClick={ viewAccessibilityProfile }
                                                aria-label="View accessibility profile"
                                            >
                                                {translate('account.passenger-assist.view-profile')}
                                            </Button>
                                            <p>{ translate('passenger-assist.modal.text') }</p>
                                        </div>
                                    </Col>
                                </Row>
                                {<ul className="passenger-assist-trip-summary-list">
                                    {journeys.map(renderJourney)}
                                    {pendingTravelAssistRequests.map(renderPassengerAssistRequest)}
                                </ul>}
                            </Col>
                        </Row>
                    </section>
                    <Row>
                        <Col xs={12}>
                            <h3 className="label" id="contact-preferences-question">
                                {translate('account.passenger-assist.accessibility-profile.form.contact-preferences.question.3')}
                            </h3>
                            <Radio
                                radioClass="regular-radio"
                                name={`${translate('account.passenger-assist.accessibility-profile.form.contact-preferences.question.3')}-${translate('account.passenger-assist.accessibility-profile.form.contact-preferences.answer.yes')}`}
                                label={translate('account.passenger-assist.accessibility-profile.form.contact-preferences.answer.yes')}
                                aria-labelledby="contact-preferences-question"
                                checked={participateImprovementSurvey}
                                onChange={onChangeParticipateImprovementSurvey(true)}
                            />
                            <Radio
                                radioClass="regular-radio"
                                name={`${translate('account.passenger-assist.accessibility-profile.form.contact-preferences.question.3')}-${translate('account.passenger-assist.accessibility-profile.form.contact-preferences.answer.no')}`}
                                label={translate('account.passenger-assist.accessibility-profile.form.contact-preferences.answer.no')}
                                aria-labelledby="contact-preferences-question"
                                checked={participateImprovementSurvey === false}
                                onChange={onChangeParticipateImprovementSurvey(false)}
                            />
                        </Col>
                    </Row>
                    {warning && (
                        <Row>
                            <Col xs={12} className="pa-assist__message--container margin-top--sm">
                                <AlertIcon className="pa-assist__icon" />
                                <p className="pa-assist__message pa-assist__message-warning">{warning}</p>
                            </Col>
                        </Row>
                    )}
                </div>
            </form>)}
        </BaseModal>
    );
}

PassengerAssistBookingModal.propTypes = {
    onClose: PropTypes.func,
    onContinue: PropTypes.func,
    router: routerShape,
    trip: PropTypes.instanceOf(Trip),
    journey: PropTypes.instanceOf(Journey),
    currentJourneyData: PropTypes.instanceOf(Journey),
    direction: PropTypes.oneOf(['Outward', 'Return'])
};


export default PassengerAssistBookingModal;
